import React, {useEffect} from "react";
import moment from "moment";
import classnames from "classnames";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {FeedbackRating, SurveyRatingGroup, SurveyTrigger} from "../../../../api/generated";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import {analyticsBotOptionsSelector, fetchAnalyticsBotOptions} from "../../analytics/analyticsSlice";
import {MultiSelectTableFilterDropdown} from "../../../elements/multiSelectTableFilter/MultiSelectTableFilterDropdown";
import {CustomDateRangePicker} from "../../../elements/datePicker/CustomDateRangePicker";
import {BroomButton} from "../../../elements/broomButton/BroomButton";
import {CustomSwitchField} from "../../../elements/customSwitchField/CustomSwitchField";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {Loader} from "../../../elements/loader/Loader";
import {useRequestErrorToast} from "../../../../hooks/useRequestErrorToast";
import {
    FEEDBACK_PAGE_SIZE,
    FeedbackTab,
    feedbackFiltersSelector,
    feedbackOverviewRequestStatusSelector,
    feedbackOverviewSelector,
    feedbackTabSelector,
    fetchFeedbackOverview,
    fetchMessageFeedbackTable,
    fetchSurveyTable,
    messageFeedbackFiltersSelector,
    messageFeedbackTableRequestStatusSelector,
    messageFeedbackTableSelector,
    resetFeedbackFilters,
    setFeedbackTab,
    setMessageFeedbackPage,
    setSurveyPage,
    surveyFiltersSelector,
    surveyTableRequestStatusSelector,
    surveyTableSelector,
    surveyTriggerOptions,
    updateFeedbackFilters,
    updateMessageFeedbackFilters,
    updateSurveyFilters
} from "../feedbackSlice";
import {FeedbackKpiTiles} from "./components/FeedbackKpiTiles";
import {MessageFeedbackTable} from "./components/MessageFeedbackTable";
import {SurveyTable} from "./components/SurveyTable";
import styles from "./FeedbackPage.module.css";

const MESSAGE_RATING_OPTIONS: { label: string, ratingList: FeedbackRating[] }[] = [
    {label: "All", ratingList: []},
    {label: "Helpful", ratingList: [FeedbackRating.Positive]},
    {label: "Not helpful", ratingList: [FeedbackRating.Negative]},
];

const SURVEY_RATING_OPTIONS: { label: string, ratingGroup?: SurveyRatingGroup }[] = [
    {label: "All"},
    {label: "Low (1–2)", ratingGroup: SurveyRatingGroup.Low},
    {label: "Mixed (3)", ratingGroup: SurveyRatingGroup.Mixed},
    {label: "High (4–5)", ratingGroup: SurveyRatingGroup.High},
];

export const FeedbackPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const tab = useAppSelector(feedbackTabSelector);
    const filters = useAppSelector(feedbackFiltersSelector);
    const messageFilters = useAppSelector(messageFeedbackFiltersSelector);
    const surveyFilters = useAppSelector(surveyFiltersSelector);
    const overview = useAppSelector(feedbackOverviewSelector);
    const messageTable = useAppSelector(messageFeedbackTableSelector);
    const messageTableStatus = useAppSelector(messageFeedbackTableRequestStatusSelector);
    const surveyTable = useAppSelector(surveyTableSelector);
    const surveyTableStatus = useAppSelector(surveyTableRequestStatusSelector);
    const botOptions = useAppSelector(analyticsBotOptionsSelector);

    useEffect(() => {
        dispatch(updateSelectedMenuItem(MenuItem.FEEDBACK));
        dispatch(fetchAnalyticsBotOptions());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchFeedbackOverview());
    }, [dispatch, filters]);

    useEffect(() => {
        if (tab === FeedbackTab.MESSAGES) {
            dispatch(fetchMessageFeedbackTable());
        }
    }, [dispatch, tab, filters, messageFilters]);

    useEffect(() => {
        if (tab === FeedbackTab.SURVEYS) {
            dispatch(fetchSurveyTable());
        }
    }, [dispatch, tab, filters, surveyFilters]);

    useRequestErrorToast(useAppSelector(feedbackOverviewRequestStatusSelector), "Failed to load the feedback overview");
    useRequestErrorToast(messageTableStatus, "Failed to load message ratings");
    useRequestErrorToast(surveyTableStatus, "Failed to load surveys");

    const showMessages = (ratingList: FeedbackRating[], withCommentOnly: boolean) => {
        dispatch(setFeedbackTab(FeedbackTab.MESSAGES));
        dispatch(updateMessageFeedbackFilters({ratingList, withCommentOnly}));
    };

    const showSurveys = (ratingGroup?: SurveyRatingGroup) => {
        dispatch(setFeedbackTab(FeedbackTab.SURVEYS));
        dispatch(updateSurveyFilters({ratingGroup, triggerList: [], withCommentOnly: false}));
    };

    const isMessagesTab = tab === FeedbackTab.MESSAGES;
    const isLoading = isMessagesTab
        ? messageTableStatus === RequestStatus.PENDING
        : surveyTableStatus === RequestStatus.PENDING;
    // Keep the rows on screen while a filter change reloads them, so the page does not jump
    const hasRows = isMessagesTab ? !!messageTable : !!surveyTable;
    const withCommentOnly = isMessagesTab ? messageFilters.withCommentOnly : surveyFilters.withCommentOnly;

    return <div className={styles.wrapper}>
        <div className={styles.header}>
            <span className={styles.title}>Feedbacks</span>
            <div className={styles.filters}>
                {botOptions.length > 1 && <MultiSelectTableFilterDropdown<string, {}>
                    value={filters.publicBotIdList}
                    placeholder={"Filter by Assistant"}
                    width={260}
                    handleChange={publicBotIdList => dispatch(updateFeedbackFilters({publicBotIdList}))}
                    options={botOptions}
                />}
                <CustomDateRangePicker
                    startDate={moment(filters.startDate)}
                    endDate={moment(filters.endDate)}
                    onChange={(start, end) => dispatch(updateFeedbackFilters({
                        startDate: start.toISOString(),
                        endDate: end.toISOString()
                    }))}
                />
                <BroomButton disabled={false} handleClick={() => dispatch(resetFeedbackFilters())}/>
            </div>
        </div>

        <FeedbackKpiTiles
            overview={overview}
            onShowAllRatings={() => showMessages([], false)}
            onShowNegativeWithComment={() => showMessages([FeedbackRating.Negative], true)}
            onShowAllSurveys={() => showSurveys()}
            onShowLowSurveys={() => showSurveys(SurveyRatingGroup.Low)}
        />

        {/* The pagination sits under the card with its own top margin, like the other tables */}
        <div className={styles.tableSection}>
            <div className={styles.card}>
                <div role="tablist" aria-label="Feedback type" className={styles.tabs}>
                    <button type="button" role="tab" aria-selected={isMessagesTab}
                            className={isMessagesTab ? styles.activeTab : styles.tab}
                            onClick={() => dispatch(setFeedbackTab(FeedbackTab.MESSAGES))}>
                        Message ratings{overview ? ` · ${overview.feedbackCount}` : ""}
                    </button>
                    <button type="button" role="tab" aria-selected={!isMessagesTab}
                            className={!isMessagesTab ? styles.activeTab : styles.tab}
                            onClick={() => dispatch(setFeedbackTab(FeedbackTab.SURVEYS))}>
                        Surveys{overview ? ` · ${overview.surveyCount}` : ""}
                    </button>
                </div>

                <div className={styles.toolbar}>
                    <span className={styles.toolbarLabel}>Rating</span>
                    {isMessagesTab
                        ? MESSAGE_RATING_OPTIONS.map(option => {
                            const isActive = option.ratingList.join() === messageFilters.ratingList.join();
                            return <button key={option.label} type="button" aria-pressed={isActive}
                                           className={isActive ? styles.activeChip : styles.chip}
                                           onClick={() => dispatch(updateMessageFeedbackFilters({ratingList: option.ratingList}))}>
                                {option.label}
                            </button>
                        })
                        : <>
                            {SURVEY_RATING_OPTIONS.map(option => {
                                const isActive = option.ratingGroup === surveyFilters.ratingGroup;
                                return <button key={option.label} type="button" aria-pressed={isActive}
                                               className={isActive ? styles.activeChip : styles.chip}
                                               onClick={() => dispatch(updateSurveyFilters({ratingGroup: option.ratingGroup}))}>
                                    {option.label}
                                </button>
                            })}
                            <MultiSelectTableFilterDropdown<SurveyTrigger, {}>
                                value={surveyFilters.triggerList}
                                placeholder={"Filter by Trigger"}
                                width={220}
                                handleChange={triggerList => dispatch(updateSurveyFilters({triggerList}))}
                                options={surveyTriggerOptions}
                            />
                        </>}
                    <div className={styles.commentToggle}>
                        <CustomSwitchField
                            textOnTrue="With comment only"
                            textOnFalse="With comment only"
                            ariaLabel="With comment only"
                            value={withCommentOnly}
                            toggleValue={value => dispatch(isMessagesTab
                                ? updateMessageFeedbackFilters({withCommentOnly: value})
                                : updateSurveyFilters({withCommentOnly: value}))}
                        />
                    </div>
                </div>

                <div className={classnames(styles.tableWrapper, isLoading && hasRows && styles.refreshing)}
                     aria-busy={isLoading}>
                    {isLoading && !hasRows
                        ? <Loader/>
                        : isMessagesTab
                            ? <MessageFeedbackTable feedback={messageTable?.feedback ?? []}/>
                            : <SurveyTable surveys={surveyTable?.surveys ?? []}/>}
                </div>
            </div>

            {isMessagesTab
                ? <TablePagination
                    pageNumber={messageFilters.pageNumber}
                    itemsPerPage={FEEDBACK_PAGE_SIZE}
                    totalItems={messageTable?.totalCount ?? 0}
                    onPageChange={page => dispatch(setMessageFeedbackPage(page))}
                />
                : <TablePagination
                    pageNumber={surveyFilters.pageNumber}
                    itemsPerPage={FEEDBACK_PAGE_SIZE}
                    totalItems={surveyTable?.totalCount ?? 0}
                    onPageChange={page => dispatch(setSurveyPage(page))}
                />}
        </div>
    </div>
}
